// One centered card: board size, opponent, color, CTA. Creates the game and
// navigates straight into it.

import { useState } from 'react';
import type { BoardSize, GameMode } from '../../shared/types.ts';
import { api, errorMessage } from '../api.ts';
import { navigate } from '../router.ts';

const LEVEL_LABELS = [
  'Beginner',
  'Gentle',
  'Casual',
  'Steady',
  'Club',
  'Sharp',
  'Strong',
  'Fierce',
  'Expert',
  'Max',
] as const;

const BOARD_CHOICES = [
  { value: 9, label: '9×9', note: 'A focused first board · 10–20 min' },
  { value: 13, label: '13×13', note: 'More room for shape · 25–40 min' },
  { value: 19, label: '19×19', note: 'The full game · 45–90 min' },
] as const;

const OPPONENT_CHOICES = [
  { value: 'bot', label: 'GNU Go', note: 'A patient computer opponent' },
  { value: 'hotseat', label: 'Hotseat', note: 'Share this board with a friend' },
] as const;

function levelHelp(level: number): string {
  if (level <= 2) return 'Forgiving play for learning liberties, capture, and connection.';
  if (level <= 4) return 'A steady learning game with room to experiment.';
  if (level <= 7) return 'Stronger shape and fewer obvious mistakes — a club-style challenge.';
  return 'Sharp, punishing play for experienced players.';
}

function MiniGrid({ n }: { n: number }) {
  const lines = [];
  for (let i = 0; i < n; i++) {
    const p = (i / (n - 1)) * 20;
    lines.push(
      <line key={`h${i}`} x1={0} y1={p} x2={20} y2={p} />,
      <line key={`v${i}`} x1={p} y1={0} x2={p} y2={20} />,
    );
  }
  return (
    <svg viewBox="-2 -2 24 24" className="mini-grid" aria-hidden="true">
      <g stroke="currentColor" strokeWidth="0.8">
        {lines}
      </g>
    </svg>
  );
}

export function SetupScreen() {
  const [boardSize, setBoardSize] = useState<BoardSize>(9);
  const [mode, setMode] = useState<GameMode>('bot');
  const [color, setColor] = useState<'B' | 'W' | 'random'>('B');
  const [botLevel, setBotLevel] = useState(3);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const begin = async () => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      const body =
        mode === 'bot'
          ? { boardSize, mode, humanColor: color, botLevel }
          : { boardSize, mode };
      const detail = await api.createGame(body);
      navigate(`/game/${detail.game.id}`);
    } catch (err) {
      setError(errorMessage(err));
      setBusy(false);
    }
  };

  return (
    <main className="setup-screen">
      <div className="setup-card">
        <h1 className="setup-title">A quiet board awaits.</h1>
        <p className="setup-sub">Set the table, choose your opponent, and begin.</p>

        <fieldset className="setup-field">
          <legend>Board</legend>
          <div className="choice-grid choice-grid-board">
            {BOARD_CHOICES.map(({ value, label, note }) => (
              <label key={value} className={`choice-card ${boardSize === value ? 'is-active' : ''}`}>
                <input
                  type="radio"
                  name="board-size"
                  value={value}
                  checked={boardSize === value}
                  onChange={() => setBoardSize(value)}
                />
                <MiniGrid n={value === 9 ? 5 : value === 13 ? 6 : 7} />
                <span className="choice-copy">
                  <span className="choice-label mono">{label}</span>
                  <span className="choice-note">{note}</span>
                </span>
              </label>
            ))}
          </div>
        </fieldset>

        <fieldset className="setup-field">
          <legend>Opponent</legend>
          <div className="choice-grid choice-grid-opponent">
            {OPPONENT_CHOICES.map(({ value, label, note }) => (
              <label key={value} className={`choice-card ${mode === value ? 'is-active' : ''}`}>
                <input
                  type="radio"
                  name="opponent"
                  value={value}
                  checked={mode === value}
                  onChange={() => setMode(value)}
                />
                <span className="choice-copy">
                  <span className="choice-label">{label}</span>
                  <span className="choice-note">{note}</span>
                </span>
              </label>
            ))}
          </div>
          {mode === 'bot' && (
            <div className="level-row">
              <div className="level-heading">
                <label htmlFor="bot-level" className="level-label">
                  Playing strength
                </label>
                <output htmlFor="bot-level" className="level-value mono">
                  {botLevel} · {LEVEL_LABELS[botLevel - 1]}
                </output>
              </div>
              <input
                id="bot-level"
                type="range"
                min={1}
                max={10}
                step={1}
                value={botLevel}
                onChange={(e) => setBotLevel(Number(e.target.value))}
                aria-describedby="bot-level-help"
              />
              <p id="bot-level-help" className="field-help">
                {levelHelp(botLevel)}
              </p>
            </div>
          )}
        </fieldset>

        {mode === 'bot' && (
          <fieldset className="setup-field">
            <legend>Your color</legend>
            <div className="color-chips">
              {(
                [
                  ['B', 'Black'],
                  ['W', 'White'],
                  ['random', 'Random'],
                ] as const
              ).map(([value, label]) => (
                <label key={value} className={`color-chip ${color === value ? 'is-active' : ''}`}>
                  <input
                    type="radio"
                    name="color"
                    value={value}
                    checked={color === value}
                    onChange={() => setColor(value)}
                  />
                  {value === 'B' && <span className="stone-icon stone-icon-b" aria-hidden="true" />}
                  {value === 'W' && <span className="stone-icon stone-icon-w" aria-hidden="true" />}
                  {value === 'random' && <span className="stone-icon stone-icon-random" aria-hidden="true" />}
                  {label}
                </label>
              ))}
            </div>
          </fieldset>
        )}

        {error && (
          <div className="setup-error" role="alert">
            <span className="setup-error-mark" aria-hidden="true">!</span>
            <span>
              <strong>We couldn&rsquo;t start that game.</strong>
              <span>{error} Check the engine status, then try again.</span>
            </span>
          </div>
        )}

        <button type="button" className="btn btn-accent btn-cta" disabled={busy} onClick={() => void begin()}>
          {busy ? <><span className="spinner" aria-hidden="true" /> Setting the stones…</> : 'Begin game'}
        </button>

        <footer className="setup-footer">
          <span>Japanese rules · Komi 6.5 · Two passes end the game</span>
          <a href="#/history">Past games →</a>
        </footer>
      </div>
    </main>
  );
}
